import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { techniciansApi, ApiError } from "../lib/api";
import {
  AvatarBadge,
  Eyebrow,
  ModernButton,
  ModernCard,
  ModernConfirmButton,
  ModernEmptyState,
  ModernErrorBanner,
  ModernPagination,
} from "../components/ui-modern";
import { Spinner } from "../components/ui-modern";

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?"
  );
}

/**
 * The modern theme's Technicians screen — see docs/FRONTEND_BACKLOG.md
 * Round 60 and `Bay2Bill Redesign.dc.html` lines ~652-683 ("Technicians").
 *
 * Same data/delete mutation as TechniciansListPageClassic.tsx — identical
 * query keys, duplicated here as a thin re-run of Classic's own calls (see
 * WorkOrdersListPageModern.tsx's own comment on why duplication over
 * extraction) — the delete mutation's `onSuccess`/`onError`/invalidated
 * query key matches Classic's exactly, so a delete here invalidates the
 * exact same cache entry Classic's own list reads from. Create now lives
 * on its own page (NewTechnicianPageModern.tsx, "+ New Technician" button
 * below) — matching the mockup's own "+ Add technician" header button
 * (and the "+ New customer"/"+ New work order" pattern the Customers/Work
 * Orders list pages already use) — rather than the inline "Add a
 * Technician" card this page used to keep at the top (Classic keeps that
 * inline form untouched on its own file).
 *
 * The mockup's card shows two stats: "In the bays now" and "Jobs all
 * time". `TechnicianListItem` (Classic's own data source, unchanged here)
 * only has a single `workOrderCount` — the same one Classic's list row
 * already shows as "N work orders" — with no separate "currently active"
 * count (that would need its own query/column, out of this round's
 * scope). Rather than fabricate a bays-now number, this shows
 * `workOrderCount` as "Jobs assigned" and, in the mockup's second stat
 * slot, the technician's own active/inactive status — the same
 * information Classic's "Inactive" tag already carries, just given a
 * second row instead of losing it.
 */
export function TechniciansListPageModern() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const filterTechnicianId = searchParams.get("technicianId") ? Number(searchParams.get("technicianId")) : null;
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { data, isLoading, error } = useQuery({
    queryKey: ["technicians", includeInactive, page, pageSize],
    queryFn: () => techniciansApi.list({ limit: pageSize, offset: (page - 1) * pageSize, includeInactive }),
    enabled: !filterTechnicianId,
  });

  const filteredQuery = useQuery({
    queryKey: ["technicians", "single", filterTechnicianId],
    queryFn: () => techniciansApi.list({ limit: 100, includeInactive: true }),
    enabled: !!filterTechnicianId,
  });

  const visibleItems = filterTechnicianId
    ? filteredQuery.data?.items.filter((t) => t.id === filterTechnicianId)
    : data?.items;
  const visibleLoading = filterTechnicianId ? filteredQuery.isLoading : isLoading;
  const visibleError = filterTechnicianId ? filteredQuery.error : error;
  const [formError, setFormError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => techniciansApi.delete(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["technicians"] }),
    onError: (err) => setFormError(err instanceof ApiError ? err.message : "Could not delete this technician."),
  });

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3.5">
        <div>
          <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">
            {/* `data.total` already IS the active count whenever
                `includeInactive` is off (the query's default state, and
                the mockup's own assumption) — the API filters to active
                technicians server-side in that mode, so no client-side
                counting/fabrication is needed. Once "Show deactivated" is
                checked, `total` covers both, so the label says so instead
                of silently mislabeling a mixed count as "active". */}
            {data ? (includeInactive ? `${data.total} total` : `${data.total} active`) : "…"}
          </Eyebrow>
          <h1 className="m-0 text-[28px] font-bold text-[#071a33]">Technicians</h1>
        </div>
        <Link to="/technicians/new">
          <ModernButton>+ New Technician</ModernButton>
        </Link>
      </div>

      {formError && <ModernErrorBanner message={formError} />}

      {filterTechnicianId ? (
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-[15px] font-semibold text-[#071a33]">Showing 1 technician from your search</h2>
          <Link to="/technicians">
            <ModernButton variant="secondary" className="min-h-0 px-3 py-1.5 text-[13px]">
              Show All Technicians
            </ModernButton>
          </Link>
        </div>
      ) : (
        <label className="mb-4 flex items-center gap-2 text-[13.5px] text-[#4a5361]">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => {
              setIncludeInactive(e.target.checked);
              setPage(1);
            }}
            className="h-4 w-4 rounded border border-[#e2e7ee]"
          />
          Show deactivated technicians
        </label>
      )}

      {visibleLoading && <Spinner />}
      {visibleError && <ModernErrorBanner message="Could not load technicians." />}
      {visibleItems && visibleItems.length === 0 && <ModernEmptyState>No technicians yet.</ModernEmptyState>}

      <div className="grid grid-cols-1 gap-3.5 sm:[grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
        {visibleItems?.map((t) => (
          <ModernCard key={t.id}>
            <div className="flex items-center gap-2.5">
              <AvatarBadge initials={initialsOf(t.displayName)} size={38} fontSize={13} />
              <span className="min-w-0">
                <span className="block truncate text-[15px] font-bold text-[#071a33]">{t.displayName}</span>
                <span className="block truncate text-[12.5px] text-[#8b97a8]">{t.code ?? "—"}</span>
              </span>
            </div>
            <dl className="mt-3.5 grid grid-cols-[1fr_auto] gap-y-1.5 text-[13px]">
              <dt className="text-[#6b7a8f]">Work orders assigned</dt>
              <dd className="m-0 text-right font-bold tabular-nums text-[#071a33]">{t.workOrderCount}</dd>
              <dt className="text-[#6b7a8f]">Status</dt>
              <dd className={`m-0 text-right font-semibold ${t.isActive ? "text-[#1a6b2c]" : "text-[#8b97a8]"}`}>
                {t.isActive ? "Active" : "Inactive"}
              </dd>
            </dl>
            <div className="mt-3.5 flex flex-wrap gap-[7px]">
              {t.workOrderCount > 0 && (
                <Link to={`/work-orders?technicianId=${t.id}`} className="flex-1">
                  <ModernButton variant="secondary" className="min-h-[38px] w-full px-3 text-[13px]">
                    View orders for this technician
                  </ModernButton>
                </Link>
              )}
              <Link to={`/technicians/${t.id}/edit`}>
                <ModernButton variant="secondary" className="min-h-[38px] px-3 text-[13px]">
                  Edit
                </ModernButton>
              </Link>
              {t.workOrderCount === 0 && (
                <ModernConfirmButton
                  variant="danger"
                  className="min-h-[38px] px-3 text-[13px]"
                  confirmText="Permanently delete this technician?"
                  disabled={deleteMutation.isPending}
                  onConfirm={() => deleteMutation.mutate(t.id)}
                >
                  Delete
                </ModernConfirmButton>
              )}
            </div>
          </ModernCard>
        ))}
      </div>

      {!filterTechnicianId && data && (
        <ModernPagination
          bordered={false}
          page={page}
          pageSize={pageSize}
          total={data.total}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPageSize(n);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}
